// The essay workshop's state machine: which case studies the learner chose,
// which step and part they are on, what each step has recorded, which steps
// are done, locked or held, and the one path every record takes to the
// platform (`onRecord`, keyed by the stable keys in lib/revise/workshop/steps.ts).
//
// Resume: `initialProgress` is the learner's newest row per key on this slide
// (services/reviseService.fetchReviseProgress). Everything restores from it,
// and the learner lands on the first step that is not finished. There is no
// stored "current step": a position row on every click would fill an
// append-only table for nothing the first-unfinished rule does not give.
//
// Each choice of case studies saves separately (Leah's instructions,
// "Choosing case studies"): the recall and match rows are keyed by case
// study, the revision, compare, rank and position rows by the chosen set, so
// switching never clears.
//
// Steps six onwards are stubs in this build (§0c); the shape here is theirs
// to grow into.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { WorkshopBlock, WorkshopLabelKey, WorkshopParagraphCount } from '../../../../../../lib/revise/types';
import {
  WORKSHOP_KEYS,
  checkLabels,
  hasChoice,
  isParagraphCount,
  numberWord,
  pickCount,
  recallKeyFacts,
  refusal,
  revisionParts,
  selectionAllowed,
  selectionKey,
  workshopSteps,
  type RevisionPart,
  type WorkshopStep,
} from '../../../../../../lib/revise/workshop/steps';
import { argumentContent, buildThesis, checkMatches, clean, compareDone, matchOrder, paragraphThemes, suggestPosition, type Judgements } from '../../../../../../lib/revise/workshop/argue';
import { seededOrder } from '../../../../../../lib/revise/scoring';
import { dealDeck, deckIds, deckSignature, type CardId } from '../../../../../../lib/revise/workshop/missed';
import { checkConclusion, checkIntro, checkParagraph, checkSelection, emptyPlan, factsFor, partIdFor, wordCount, type CheckItem, type FactOnOffer, type ParagraphPlan, type PartId, assembleEssay, essayParts, joinList, partTitle } from '../../../../../../lib/revise/workshop/write';

export interface RecallState {
  text: string;
  /** The learner has checked their list; the key facts are shown. */
  done: boolean;
  /** Indices of the key facts they marked as had. */
  got: number[];
  /** The check is saved; the screen releases its hold. */
  saved: boolean;
}

export interface MatchState {
  /** Theme index → the evidence index placed beside it. */
  map: Record<number, number>;
  /** The last check's marks are showing. */
  checked: boolean;
  checks: number;
  solved: boolean;
}

export interface ArgueState {
  /** Case study → theme → judgement index on the scale. */
  cmp: Judgements;
  rank: number[];
  ranked: boolean;
  nparas: WorkshopParagraphCount | null;
  pos: number | null;
  reason: number | null;
  counter: number | null;
  ownR: string;
  ownC: string;
  line: string;
}

export interface MissedState {
  /** The deck this pile was dealt from; a different deck deals again. */
  sig: string;
  /** Cards still to get, front first. */
  queue: CardId[];
  got: CardId[];
  /** The front card is turned to its answer. */
  flip: boolean;
}

export interface DraftState {
  text: string;
  /** The quick check has run on this text. */
  checked: boolean;
  /** The learner ticked "I have written it by hand". Never gates. */
  hand: boolean;
}

/** How long a draft waits before it is saved while the learner keeps typing. */
export const DRAFT_SAVE_MS = 20_000;

export interface UseWorkshopOptions {
  w: WorkshopBlock;
  initialProgress?: Record<string, Record<string, unknown>> | null;
  /** One record per event, keyed; isCorrect/score ride along for the labels check. */
  onRecord?: (key: string, payload: Record<string, unknown>, isCorrect?: boolean, score?: number) => void;
  /** A draft save: one row per key, overwritten (services/reviseService.saveReviseDraft). */
  onDraft?: (key: string, payload: Record<string, unknown>) => void;
}

export type WorkshopPhase = 'start' | 'steps';

const recallKey = (caseStudy: string, page: number) => `${caseStudy}:${page}`;
const emptyMatch = (): MatchState => ({ map: {}, checked: false, checks: 0, solved: false });

export function useWorkshop({ w, initialProgress, onRecord, onDraft }: UseWorkshopOptions) {
  const fixed = 'fixed' in w.selection ? w.selection.fixed : null;
  const pick = pickCount(w);

  const [phase, setPhase] = useState<WorkshopPhase>('start');
  const [stepIdx, setStepIdx] = useState(0);
  const [part, setPart] = useState(0);
  const [chosen, setChosen] = useState<string[]>(fixed ?? []);
  const [refused, setRefused] = useState<string | null>(null);
  const [tags, setTags] = useState<Record<number, WorkshopLabelKey | undefined>>({});
  const [brush, setBrushState] = useState<WorkshopLabelKey>(w.labels[0]?.key ?? 'command');
  // The brush is read through a ref so two taps in one task (pick a label,
  // tag a part) never see a stale label.
  const brushRef = useRef(brush);
  const setBrush = useCallback((k: WorkshopLabelKey) => {
    brushRef.current = k;
    setBrushState(k);
  }, []);
  const [labelCheck, setLabelCheck] = useState<ReturnType<typeof checkLabels> | null>(null);
  const [recall, setRecall] = useState<Record<string, RecallState>>({});
  const [revDone, setRevDone] = useState<Record<string, boolean>>({});
  const [revSkipped, setRevSkipped] = useState<Record<string, boolean>>({});
  const [match, setMatch] = useState<Record<string, MatchState>>({});
  const [argue, setArgue] = useState<Record<string, ArgueState>>({});
  const [missedState, setMissedState] = useState<Record<string, MissedState>>({});
  const [plans, setPlans] = useState<Record<string, Record<number, ParagraphPlan>>>({});
  const [drafts, setDrafts] = useState<Record<string, Partial<Record<PartId, DraftState>>>>({});
  const [checks, setChecks] = useState<Record<string, Partial<Record<PartId, CheckItem[]>>>>({});
  const restored = useRef(false);
  /** Set once the learner has been placed on a step, so the landing effect runs only for a restore. */
  const landed = useRef(false);

  /** Every key recorded this sitting, so "clear everything" can mark each one cleared. */
  const seenKeys = useRef<Set<string>>(new Set());
  const record = useCallback(
    (key: string, payload: Record<string, unknown>, isCorrect?: boolean, score?: number) => {
      seenKeys.current.add(key);
      onRecord?.(key, { questionId: key, ...payload }, isCorrect, score);
    },
    [onRecord],
  );

  const complete = selectionAllowed(w, chosen);
  const selKey = complete ? selectionKey(chosen) : null;
  const first = complete ? w.editions[chosen[0]] : undefined;
  const n = first?.themes.length ?? 0;

  // --- The argument: content and the learner's choices --------------------
  const content = useMemo(() => (complete ? argumentContent(w, chosen) : null), [w, chosen, complete]);
  const freshArgue = useCallback((): ArgueState => ({ cmp: {}, rank: seededOrder(n, `${selKey}|rank`), ranked: false, nparas: null, pos: null, reason: null, counter: null, ownR: '', ownC: '', line: '' }), [n, selKey]);
  const arg: ArgueState = useMemo(() => (selKey && argue[selKey]) || freshArgue(), [argue, selKey, freshArgue]);
  const position = arg.pos !== null ? content?.positions[arg.pos] : undefined;
  const reasonText = clean(arg.ownR) || (position && arg.reason !== null ? clean(position.reasons[arg.reason]?.text ?? '') : '');
  const counterText = clean(arg.ownC) || (position && arg.counter !== null ? clean(position.counters[arg.counter]?.text ?? '') : '');
  const reasonTheme = clean(arg.ownR) || !position || arg.reason === null ? undefined : position.reasons[arg.reason]?.theme;
  const counterTheme = clean(arg.ownC) || !position || arg.counter === null ? undefined : position.counters[arg.counter]?.theme;
  const paragraphs = arg.nparas;
  const twoCase = chosen.length === 2;
  const cmpDone = twoCase && compareDone(arg.cmp, chosen, n);
  const argDone = !!arg.line.trim() && arg.pos !== null && !!counterText && arg.ranked && !!paragraphs && (!twoCase || cmpDone);
  const suggestion = useMemo(
    () => (content ? suggestPosition(content.hints, { judgements: arg.cmp, chosen, rank: arg.rank, ranked: arg.ranked, n, counterTheme: first?.counterTheme, anchorTheme: first?.anchorTheme }) : null),
    [content, arg.cmp, arg.rank, arg.ranked, chosen, n, first],
  );
  const paragraphList = useMemo(() => (paragraphs ? paragraphThemes(arg.rank, paragraphs, [first?.anchorTheme, reasonTheme, counterTheme]) : []), [arg.rank, paragraphs, first, reasonTheme, counterTheme]);

  const steps = useMemo(() => workshopSteps(w, paragraphs ?? 4), [w, paragraphs]);
  /** The written parts in essay order. */
  const essayOrder = useMemo(() => essayParts(paragraphList), [paragraphList]);

  // --- The paragraphs: plans and drafts --------------------------------------
  const planOf = useCallback((theme: number): ParagraphPlan => (selKey && plans[selKey]?.[theme]) || emptyPlan(), [plans, selKey]);
  const draftOf = useCallback((part: PartId): DraftState => (selKey && drafts[selKey]?.[part]) || { text: '', checked: false, hand: false }, [drafts, selKey]);
  const topicSentenceOf = useCallback(
    (theme: number): string => {
      const p = planOf(theme);
      if (p.ts === 'own') return clean(p.own) ? `${clean(p.own)}.` : '';
      return p.ts !== null ? content?.topicSentences[theme]?.[p.ts]?.text ?? '' : '';
    },
    [planOf, content],
  );
  /** The historians on offer for a theme, across the chosen case studies. */
  const perspectivesFor = useCallback(
    (theme: number) =>
      chosen.flatMap((k) => {
        const e = w.editions[k];
        return (e?.perspectives[theme] ?? []).map((p) => ({ ...p, caseStudy: k, name: e.historians[p.historian]?.name ?? p.historian, opener: e.historians[p.historian]?.opener ?? '', match: e.historians[p.historian]?.match ?? [] }));
      }),
    [w, chosen],
  );
  const perspectiveOf = useCallback(
    (theme: number) => {
      const p = planOf(theme);
      if (p.noP || p.persp === null) return null;
      return perspectivesFor(theme)[p.persp] ?? null;
    },
    [planOf, perspectivesFor],
  );
  const perspDone = useCallback(
    (theme: number) => {
      const p = planOf(theme);
      return p.noP || (!!perspectiveOf(theme) && p.puse !== null && p.pagree !== null);
    },
    [planOf, perspectiveOf],
  );
  const planDone = useCallback((theme: number) => !!topicSentenceOf(theme) && planOf(theme).locked && perspDone(theme), [topicSentenceOf, planOf, perspDone]);
  const partDone = useCallback(
    (part: PartId) => {
      const d = draftOf(part);
      return d.checked && !!d.text.trim();
    },
    [draftOf],
  );
  const factsOffered = useCallback((theme: number): FactOnOffer[] => (complete ? factsFor(w, chosen, theme, paragraphList) : []), [w, chosen, complete, paragraphList]);
  /** The theme a paragraph step writes about, by its number (1-based). */
  const themeOfParagraph = useCallback((n: number): number | undefined => paragraphList[n - 1], [paragraphList]);

  // --- The flashcard deck: the key facts missed on the saved recall screens --
  const missedByScreen = useMemo(() => {
    const out: Array<{ caseStudy: string; page: number; missed: number[] }> = [];
    for (const k of chosen) {
      const e = w.editions[k];
      if (!e) continue;
      e.revision.forEach((p, i) => {
        const st = recall[recallKey(k, i)];
        if (!p.recall || !st?.saved) return;
        // The same resolved list the recall screen ticked and the card resolver reads.
        const total = recallKeyFacts(e, p).length;
        const missed = Array.from({ length: total }, (_, j) => j).filter((j) => !st.got.includes(j));
        if (missed.length) out.push({ caseStudy: k, page: i, missed });
      });
    }
    return out;
  }, [w, chosen, recall]);
  const anyRecalled = chosen.some((k) => Object.keys(recall).some((rk) => rk.startsWith(`${k}:`) && recall[rk].saved));
  const deck = useMemo(() => (complete ? deckIds(w, chosen, missedByScreen, !!selKey && !!revSkipped[selKey], anyRecalled) : []), [w, chosen, complete, missedByScreen, selKey, revSkipped, anyRecalled]);
  const deckSig = deckSignature(deck);
  /** The pile for the current deck: the stored one when it was dealt from this deck, else a fresh deal. */
  const pile: MissedState = useMemo(() => {
    const stored = selKey ? missedState[selKey] : undefined;
    if (stored && stored.sig === deckSig) return stored;
    return { sig: deckSig, queue: dealDeck(deck, selKey ?? ''), got: [], flip: false };
  }, [missedState, selKey, deckSig, deck]);
  const missedDone = complete && pile.queue.length === 0;

  // --- Done / locked --------------------------------------------------------
  const matchSolved = complete && chosen.every((k) => match[k]?.solved);
  const isDone = useCallback(
    (s: WorkshopStep): boolean => {
      switch (s.id) {
        case 'question':
          return !!labelCheck?.ok;
        case 'revise':
          // A skipped revision opens the later steps (the rail still says
          // "skipped" and the learner can come back to it at any time).
          return !!selKey && (!!revDone[selKey] || !!revSkipped[selKey]);
        case 'match':
          return matchSolved;
        case 'argue':
          return argDone;
        case 'missed':
          return missedDone;
        case 'paragraphs':
          return argDone;
        case 'intro':
          return partDone('intro');
        case 'paragraph': {
          const theme = s.n !== undefined ? themeOfParagraph(s.n) : undefined;
          return theme !== undefined && planDone(theme) && partDone(partIdFor(theme));
        }
        case 'conclusion':
          return partDone('conclusion');
        case 'essay':
          return argDone && essayOrder.every(partDone);
        default:
          return false;
      }
    },
    [labelCheck, selKey, revDone, revSkipped, matchSolved, argDone, missedDone, partDone, planDone, themeOfParagraph, essayOrder],
  );

  /** The first step not finished; everything after it is locked except the essay view. */
  const firstOpen = useMemo(() => {
    for (let i = 0; i < steps.length - 1; i++) if (!isDone(steps[i])) return i;
    return steps.length - 1;
  }, [steps, isDone]);
  const isLocked = useCallback((i: number) => i > firstOpen && steps[i]?.id !== 'essay', [firstOpen, steps]);
  /** The first unfinished part of a paragraph step, so a resume opens where the plan stopped. */
  const firstOpenPart = useCallback(
    (i: number): number => {
      const st = steps[i];
      if (st?.id !== 'paragraph') return 0;
      const theme = themeOfParagraph(st.n ?? 1);
      if (theme === undefined) return 0;
      if (!topicSentenceOf(theme)) return 0;
      if (!planOf(theme).locked) return 1;
      if (!perspDone(theme)) return 2;
      return 3;
    },
    [steps, themeOfParagraph, topicSentenceOf, planOf, perspDone],
  );

  // --- Resume ---------------------------------------------------------------
  useEffect(() => {
    if (restored.current || !initialProgress) return;
    restored.current = true;
    // A row marked cleared is the record of "clear everything and start again": it is not restored.
    const live: Record<string, Record<string, unknown>> = Object.fromEntries(Object.entries(initialProgress).filter(([, row]) => !row || row.cleared !== true));
    let any = false;
    const q = live[WORKSHOP_KEYS.question];
    if (q && q.tags && typeof q.tags === 'object') {
      const t: Record<number, WorkshopLabelKey> = {};
      for (const [k, v] of Object.entries(q.tags as Record<string, unknown>)) if (typeof v === 'string') t[Number(k)] = v as WorkshopLabelKey;
      setTags(t);
      setLabelCheck(checkLabels(w, t));
      any = true;
    }
    if (!fixed) {
      const c = live[WORKSHOP_KEYS.choose];
      const cases = Array.isArray(c?.cases) ? (c!.cases as unknown[]).filter((x): x is string => typeof x === 'string') : [];
      if (cases.length && selectionAllowed(w, cases)) {
        setChosen(cases);
        any = true;
      }
    }
    const rd: Record<string, boolean> = {};
    const rs: Record<string, boolean> = {};
    const ms: Record<string, MatchState> = {};
    const as: Record<string, ArgueState> = {};
    const mis: Record<string, MissedState> = {};
    const pl: Record<string, Record<number, ParagraphPlan>> = {};
    const dr: Record<string, Partial<Record<PartId, DraftState>>> = {};
    const argOf = (sel: string): ArgueState => as[sel] ?? (as[sel] = { cmp: {}, rank: [], ranked: false, nparas: null, pos: null, reason: null, counter: null, ownR: '', ownC: '', line: '' });
    const num = (v: unknown): number | null => (typeof v === 'number' && Number.isInteger(v) ? v : null);
    for (const [k, row] of Object.entries(live)) {
      if (k.startsWith('ws:revise:')) {
        const sel = k.slice('ws:revise:'.length);
        if (row.done) rd[sel] = true;
        if (row.skipped) rs[sel] = true;
        any = true;
      } else if (k.startsWith('ws:match:')) {
        const cs = k.slice('ws:match:'.length);
        if (!w.editions[cs]) continue;
        const map: Record<number, number> = {};
        if (row.map && typeof row.map === 'object') for (const [t, e] of Object.entries(row.map as Record<string, unknown>)) if (typeof e === 'number') map[Number(t)] = e;
        ms[cs] = { map, checked: Object.keys(map).length > 0, checks: num(row.checks) ?? 0, solved: !!row.solved };
        any = true;
      } else if (k.startsWith('ws:compare:')) {
        const a = argOf(k.slice('ws:compare:'.length));
        if (row.cmp && typeof row.cmp === 'object') {
          for (const [cs, byTheme] of Object.entries(row.cmp as Record<string, unknown>)) {
            if (!byTheme || typeof byTheme !== 'object') continue;
            a.cmp[cs] = {};
            for (const [t, v] of Object.entries(byTheme as Record<string, unknown>)) if (typeof v === 'number') a.cmp[cs][Number(t)] = v;
          }
        }
        any = true;
      } else if (k.startsWith('ws:rank:')) {
        const a = argOf(k.slice('ws:rank:'.length));
        if (Array.isArray(row.rank)) a.rank = (row.rank as unknown[]).map(Number);
        a.ranked = !!row.ranked;
        if (isParagraphCount(row.nparas)) a.nparas = row.nparas;
        any = true;
      } else if (k.startsWith('ws:missed:')) {
        const sel = k.slice('ws:missed:'.length);
        if (typeof row.sig === 'string' && Array.isArray(row.queue) && Array.isArray(row.got)) {
          mis[sel] = { sig: row.sig, queue: (row.queue as unknown[]).filter((x): x is string => typeof x === 'string'), got: (row.got as unknown[]).filter((x): x is string => typeof x === 'string'), flip: false };
          any = true;
        }
      } else if (k.startsWith('ws:plan:')) {
        const [sel, themeStr] = k.slice('ws:plan:'.length).split(':');
        const theme = Number(themeStr);
        if (!Number.isInteger(theme)) continue;
        const p: ParagraphPlan = {
          ts: row.ts === 'own' ? 'own' : num(row.ts),
          own: typeof row.own === 'string' ? row.own : '',
          sel: Array.isArray(row.sel) ? (row.sel as unknown[]).filter((x): x is string => typeof x === 'string') : [],
          checked: !!row.locked,
          locked: !!row.locked,
          persp: num(row.persp),
          noP: !!row.noP,
          puse: num(row.puse),
          pagree: num(row.pagree),
        };
        pl[sel] = { ...(pl[sel] ?? {}), [theme]: p };
        any = true;
      } else if (k.startsWith('ws:draft:')) {
        const [sel, part] = k.slice('ws:draft:'.length).split(':');
        if (!part) continue;
        dr[sel] = { ...(dr[sel] ?? {}), [part as PartId]: { text: typeof row.text === 'string' ? row.text : '', checked: !!row.checked, hand: !!row.hand } };
        any = true;
      } else if (k.startsWith('ws:position:')) {
        const a = argOf(k.slice('ws:position:'.length));
        a.pos = num(row.pos);
        a.reason = num(row.reason);
        a.counter = num(row.counter);
        a.ownR = typeof row.ownR === 'string' ? row.ownR : '';
        a.ownC = typeof row.ownC === 'string' ? row.ownC : '';
        a.line = typeof row.line === 'string' ? row.line : '';
        any = true;
      }
    }
    const rc: Record<string, RecallState> = {};
    for (const [k, ex] of Object.entries(w.editions)) {
      ex.revision.forEach((p, i) => {
        if (!p.recall) return;
        const row = live[WORKSHOP_KEYS.recall(k, i)];
        if (!row) return;
        rc[recallKey(k, i)] = {
          text: typeof row.text === 'string' ? row.text : '',
          done: true,
          got: Array.isArray(row.got) ? (row.got as unknown[]).map(Number) : [],
          saved: !!row.saved,
        };
        any = true;
      });
    }
    if (Object.keys(rc).length) setRecall(rc);
    if (Object.keys(rd).length) setRevDone(rd);
    if (Object.keys(rs).length) setRevSkipped(rs);
    if (Object.keys(ms).length) setMatch(ms);
    if (Object.keys(as).length) setArgue(as);
    if (Object.keys(mis).length) setMissedState(mis);
    if (Object.keys(pl).length) setPlans(pl);
    if (Object.keys(dr).length) setDrafts(dr);
    if (any) setPhase('steps');
  }, [initialProgress, w, fixed]);

  // A restored rank with the wrong length (or none) gets a fresh deal.
  useEffect(() => {
    if (!selKey || !n) return;
    const a = argue[selKey];
    if (a && a.rank.length !== n) setArgue((s) => ({ ...s, [selKey]: { ...a, rank: seededOrder(n, `${selKey}|rank`), ranked: false } }));
  }, [argue, selKey, n]);

  // Land on the first unfinished step when work is restored.
  useEffect(() => {
    if (phase !== 'steps' || landed.current) return;
    landed.current = true;
    setStepIdx(firstOpen);
    setPart(firstOpenPart(firstOpen));
  }, [phase, firstOpen, firstOpenPart]);

  // --- Parts of the current step -------------------------------------------
  const step = steps[stepIdx];
  const parts: RevisionPart[] = useMemo(() => (complete ? revisionParts(w, chosen) : []), [w, chosen, complete]);
  const partCountFor = useCallback(
    (s: WorkshopStep | undefined) => {
      if (!s) return 1;
      if (s.id === 'question') return 2;
      if (s.id === 'revise') return Math.max(1, parts.length);
      if (s.id === 'match') return complete ? chosen.length + 1 : 1;
      if (s.id === 'argue') return complete ? (twoCase ? 4 : 3) : 1;
      if (s.id === 'paragraph') return complete && s.n !== undefined && themeOfParagraph(s.n) !== undefined ? 4 : 1;
      return 1;
    },
    [parts, complete, chosen.length, twoCase, themeOfParagraph],
  );
  const partCount = partCountFor(step);

  /** Which screen of the argue step this part is. */
  const arguePart: 'readings' | 'compare' | 'rank' | 'position' | null = step?.id !== 'argue' || !complete ? null : part === 0 ? 'readings' : twoCase ? (['compare', 'rank', 'position'] as const)[part - 1] ?? null : (['rank', 'position'] as const)[part - 1] ?? null;
  /** Which board of the match step this part is, or null for the table. */
  const matchCase: string | null = step?.id === 'match' && complete && part < chosen.length ? chosen[part] : null;

  const currentRecall = useMemo(() => {
    if (step?.id !== 'revise') return null;
    const p = parts[part];
    if (!p || p.kind !== 'recall') return null;
    const key = recallKey(p.caseStudy, p.page);
    return { caseStudy: p.caseStudy, page: p.page, key, state: recall[key] ?? { text: '', done: false, got: [], saved: false } };
  }, [step, parts, part, recall]);

  /** On a recall screen the learner has not checked: nothing else can be opened. */
  const held = !!currentRecall && !currentRecall.state.done;

  /** Why the learner cannot leave this part yet, or null. */
  const gate: string | null = useMemo(() => {
    if (!step) return null;
    const chooseFirst = `Choose your ${pick === 2 ? `two ${w.unit.plural}` : w.unit.singular} on the start screen first.`;
    if (step.id === 'question' && part === 0 && !labelCheck?.ok) return `Label all ${w.parts.length} parts correctly to go on.`;
    if ((step.id === 'revise' || step.id === 'match' || step.id === 'argue') && !complete) return chooseFirst;
    if (currentRecall && !currentRecall.state.saved) return 'Write what you remember, check it and save your check to go on.';
    if (matchCase && !match[matchCase]?.solved) return `Match all ${n} to go on.`;
    if (arguePart === 'compare' && !cmpDone) return `Judge both ${w.unit.plural} on all ${n} ${w.themeWord.plural} to go on.`;
    if (arguePart === 'rank' && (!arg.ranked || !paragraphs)) return `Rank the ${w.themeWord.plural} and choose how many body paragraphs you will write to go on.`;
    if (arguePart === 'position' && !argDone) return 'Choose your position, your main reason and your counter-argument to go on.';
    if ((step.id === 'paragraphs' || step.id === 'paragraph' || step.id === 'intro' || step.id === 'conclusion') && complete && !argDone) return 'Decide your argument first.';
    if (step.id === 'paragraph' && complete && step.n !== undefined) {
      const theme = themeOfParagraph(step.n);
      if (theme === undefined) return 'Decide your argument first.';
      if (part === 0 && !topicSentenceOf(theme)) return 'Choose a topic sentence to go on.';
      if (part === 1 && !planOf(theme).locked) return 'Select your facts and check them to go on.';
      if (part === 2 && !perspDone(theme)) return 'Choose a perspective and decide how you will use it, or leave it out, to go on.';
      if (part === 3 && !partDone(partIdFor(theme))) return 'Write your paragraph and check it to go on.';
    }
    if (step.id === 'intro' && complete && !partDone('intro')) return 'Write your introduction and check it to go on.';
    if (step.id === 'conclusion' && complete && !partDone('conclusion')) return 'Write your conclusion and check it to go on.';
    const last = part + 1 >= partCount;
    // The essay is the end: nothing to go on to, so nothing to gate.
    if (last && !isDone(step) && step.id !== 'revise' && step.id !== 'essay' && step.built) return 'Finish this step to go on.';
    return null;
  }, [step, part, labelCheck, complete, pick, w, currentRecall, matchCase, match, n, arguePart, cmpDone, arg.ranked, paragraphs, argDone, partCount, isDone, themeOfParagraph, topicSentenceOf, planOf, perspDone, partDone]);

  // Reaching the last part of the revision marks it done (and un-skips it).
  useEffect(() => {
    if (phase !== 'steps' || step?.id !== 'revise' || !selKey) return;
    if (part + 1 < partCount) return;
    if (revDone[selKey]) return;
    setRevDone((d) => ({ ...d, [selKey]: true }));
    setRevSkipped((d) => ({ ...d, [selKey]: false }));
    record(WORKSHOP_KEYS.revise(selKey), { cases: chosen, done: true, skipped: false });
  }, [phase, step, selKey, chosen, part, partCount, revDone, record]);

  // --- Navigation -----------------------------------------------------------
  const begin = useCallback(() => {
    if (!complete) return;
    landed.current = true;
    setPhase('steps');
    setStepIdx(firstOpen);
    setPart(firstOpenPart(firstOpen));
  }, [complete, firstOpen, firstOpenPart]);

  const goTo = useCallback(
    (i: number, p = 0) => {
      if (held) return;
      const target = isLocked(i) ? firstOpen : Math.max(0, Math.min(i, steps.length - 1));
      setStepIdx(target);
      setPart(target === i ? Math.max(0, p) : 0);
    },
    [held, isLocked, firstOpen, steps.length],
  );
  const goToStep = useCallback((key: string, p = 0) => goTo(steps.findIndex((s) => s.key === key), p), [goTo, steps]);

  const next = useCallback(() => {
    if (gate) return;
    if (part + 1 < partCount) setPart(part + 1);
    else if (stepIdx + 1 < steps.length) {
      setStepIdx(stepIdx + 1);
      setPart(0);
    }
  }, [gate, part, partCount, stepIdx, steps.length]);

  const prev = useCallback(() => {
    if (held) return;
    if (part > 0) setPart(part - 1);
    else if (stepIdx > 0) {
      const target = stepIdx - 1;
      setStepIdx(target);
      setPart(partCountFor(steps[target]) - 1);
    }
  }, [held, part, stepIdx, steps, partCountFor]);

  const toStart = useCallback(() => {
    if (held) return;
    setPhase('start');
  }, [held]);

  // --- The start screen: the choice of case studies -------------------------
  /** Choose or un-choose a case study. A refused pairing sets `refused` with the reason. */
  const toggleCase = useCallback(
    (id: string) => {
      if (!hasChoice(w) || !w.editions[id]) return;
      setRefused(null);
      setChosen((cur) => {
        if (cur.includes(id)) return cur.filter((x) => x !== id);
        if (pick === 1) return [id];
        if (cur.length === 0) return [id];
        const why = refusal(w, cur[0], id);
        if (why) {
          setRefused(why);
          return cur;
        }
        return cur.length >= 2 ? [cur[1], id] : [cur[0], id];
      });
    },
    [w, pick],
  );
  // Record a completed choice whenever it changes.
  const lastRecorded = useRef<string | null>(fixed ? selectionKey(fixed) : null);
  useEffect(() => {
    if (!complete || !selKey || fixed) return;
    if (lastRecorded.current === selKey) return;
    lastRecorded.current = selKey;
    record(WORKSHOP_KEYS.choose, { cases: chosen });
  }, [complete, selKey, chosen, fixed, record]);

  // --- Step 1: the labels ---------------------------------------------------
  const tagPart = useCallback((index: number) => {
    const b = brushRef.current;
    setTags((t) => ({ ...t, [index]: t[index] === b ? undefined : b }));
    setLabelCheck(null);
  }, []);
  const checkTags = useCallback(() => {
    const result = checkLabels(w, tags);
    setLabelCheck(result);
    record(WORKSHOP_KEYS.question, { step: 'question', tags, correct: result.correct, total: result.total, ok: result.ok }, result.ok, result.total ? result.correct / result.total : 0);
  }, [w, tags, record]);

  // --- Step 2: revision and recall ------------------------------------------
  const setRecallText = useCallback(
    (text: string) => {
      if (!currentRecall) return;
      const { key } = currentRecall;
      setRecall((r) => ({ ...r, [key]: { ...(r[key] ?? { done: false, got: [], saved: false }), text } }));
    },
    [currentRecall],
  );
  const checkRecall = useCallback(() => {
    if (!currentRecall) return;
    const { key, caseStudy, page, state } = currentRecall;
    setRecall((r) => ({ ...r, [key]: { ...state, ...r[key], done: true } }));
    record(WORKSHOP_KEYS.recall(caseStudy, page), { step: 'revise', caseStudy, page, text: state.text, done: true, got: [], saved: false });
  }, [currentRecall, record]);
  const toggleRecallFact = useCallback(
    (j: number) => {
      if (!currentRecall) return;
      const { key } = currentRecall;
      setRecall((r) => {
        const cur = r[key];
        if (!cur || cur.saved) return r;
        const got = cur.got.includes(j) ? cur.got.filter((x) => x !== j) : [...cur.got, j];
        return { ...r, [key]: { ...cur, got } };
      });
    },
    [currentRecall],
  );
  const saveRecall = useCallback(
    (total: number) => {
      if (!currentRecall) return;
      const { key, caseStudy, page } = currentRecall;
      const cur = recall[key];
      if (!cur) return;
      setRecall((r) => ({ ...r, [key]: { ...r[key], saved: true } }));
      record(WORKSHOP_KEYS.recall(caseStudy, page), { step: 'revise', caseStudy, page, text: cur.text, done: true, got: cur.got, total, saved: true });
    },
    [currentRecall, recall, record],
  );
  const changeRecall = useCallback(() => {
    if (!currentRecall) return;
    const { key } = currentRecall;
    setRecall((r) => (r[key] ? { ...r, [key]: { ...r[key], saved: false } } : r));
  }, [currentRecall]);

  /** Skip the revision, from the start screen or its first screen, and go to the match step. */
  const skipRevision = useCallback(() => {
    if (!complete || !selKey) return;
    setRevSkipped((d) => ({ ...d, [selKey]: true }));
    record(WORKSHOP_KEYS.revise(selKey), { cases: chosen, done: false, skipped: true });
    landed.current = true;
    setPhase('steps');
    const matchIdx = steps.findIndex((s) => s.id === 'match');
    setStepIdx(matchIdx > 0 ? matchIdx : 0);
    setPart(0);
  }, [complete, selKey, chosen, record, steps]);

  // Write-through mirrors of the step states, so two taps in one task (keep
  // the order, then choose four) build on each other and not on the render
  // both of them saw.
  const latestMatch = useRef<Record<string, MatchState>>({});
  const latestArgue = useRef<Record<string, ArgueState>>({});
  const latestPile = useRef<Record<string, MissedState>>({});
  useEffect(() => {
    latestMatch.current = match;
  }, [match]);
  useEffect(() => {
    latestArgue.current = argue;
  }, [argue]);
  useEffect(() => {
    latestPile.current = missedState;
  }, [missedState]);

  // --- Step 3: match --------------------------------------------------------
  const [matchSel, setMatchSel] = useState<number | null>(null);
  const matchState: MatchState = (matchCase && match[matchCase]) || emptyMatch();
  const matchDeal = useMemo(() => (matchCase ? matchOrder(n, `${matchCase}|match`) : []), [matchCase, n]);
  const getMatch = useCallback((): MatchState => (matchCase && latestMatch.current[matchCase]) || emptyMatch(), [matchCase]);
  const commitMatch = useCallback(
    (next: MatchState) => {
      if (!matchCase) return;
      latestMatch.current = { ...latestMatch.current, [matchCase]: next };
      setMatch((s) => ({ ...s, [matchCase]: next }));
    },
    [matchCase],
  );
  /** Tap a theme: select it, or undo its match. */
  const tapTheme = useCallback(
    (i: number) => {
      const m = getMatch();
      if (!matchCase || m.solved) return;
      if (m.map[i] !== undefined) {
        const map = { ...m.map };
        delete map[i];
        commitMatch({ ...m, map, checked: false });
      } else setMatchSel((s) => (s === i ? null : i));
    },
    [matchCase, getMatch, commitMatch],
  );
  /** Tap an evidence line: pair it with the selected theme, or undo its match. */
  const tapEvidence = useCallback(
    (ri: number) => {
      const m = getMatch();
      if (!matchCase || m.solved) return;
      const li = Object.keys(m.map).map(Number).find((t) => m.map[t] === ri);
      if (li !== undefined) {
        const map = { ...m.map };
        delete map[li];
        commitMatch({ ...m, map, checked: false });
      } else if (matchSel !== null) {
        commitMatch({ ...m, map: { ...m.map, [matchSel]: ri }, checked: false });
        setMatchSel(null);
      }
    },
    [matchCase, matchSel, getMatch, commitMatch],
  );
  const [matchNote, setMatchNote] = useState<{ ok: boolean; text: string } | null>(null);
  const checkMatch = useCallback(() => {
    if (!matchCase) return;
    const m = getMatch();
    const c = checkMatches(m.map, n);
    if (c.missing > 0) {
      setMatchNote({ ok: false, text: `${c.missing} still to match.` });
      return;
    }
    const checks = m.checks + 1;
    commitMatch({ ...m, checked: true, checks, solved: c.solved });
    setMatchNote(c.solved ? { ok: true, text: `All ${numberWord(n)} matched.` } : { ok: false, text: `${c.correct} of ${n} matched correctly. Undo the ones marked and try again.` });
    record(WORKSHOP_KEYS.match(matchCase), { step: 'match', caseStudy: matchCase, map: m.map, checks, correct: c.correct, total: n, solved: c.solved }, c.solved, c.correct / n);
  }, [matchCase, n, getMatch, commitMatch, record]);
  const showMatches = useCallback(() => {
    if (!matchCase) return;
    const m = getMatch();
    const map: Record<number, number> = {};
    for (let i = 0; i < n; i++) map[i] = i;
    commitMatch({ ...m, map, checked: true, solved: true });
    setMatchNote({ ok: true, text: `Here are the ${numberWord(n)} matches. Read each pair before you move on.` });
    record(WORKSHOP_KEYS.match(matchCase), { step: 'match', caseStudy: matchCase, map, checks: m.checks, shown: true, solved: true });
  }, [matchCase, n, getMatch, commitMatch, record]);
  // The note belongs to one board.
  useEffect(() => {
    setMatchNote(null);
    setMatchSel(null);
  }, [matchCase]);

  // --- Step 4: argue --------------------------------------------------------
  const getArg = useCallback((): ArgueState => (selKey && latestArgue.current[selKey]) || arg, [selKey, arg]);
  const commitArgue = useCallback(
    (next: ArgueState) => {
      if (!selKey) return;
      latestArgue.current = { ...latestArgue.current, [selKey]: next };
      setArgue((s) => ({ ...s, [selKey]: next }));
    },
    [selKey],
  );
  const judge = useCallback(
    (caseStudy: string, theme: number, value: number) => {
      if (!selKey) return;
      const cur = getArg();
      const cmp: Judgements = { ...cur.cmp, [caseStudy]: { ...(cur.cmp[caseStudy] ?? {}), [theme]: value } };
      commitArgue({ ...cur, cmp });
      if (compareDone(cmp, chosen, n)) record(WORKSHOP_KEYS.compare(selKey), { step: 'argue', cases: chosen, cmp });
    },
    [selKey, chosen, n, getArg, commitArgue, record],
  );
  const recordRank = useCallback(
    (a: ArgueState) => {
      if (selKey) record(WORKSHOP_KEYS.rank(selKey), { step: 'argue', cases: chosen, rank: a.rank, ranked: a.ranked, nparas: a.nparas, paragraphs: paragraphThemes(a.rank, a.nparas ?? 4, [first?.anchorTheme, reasonTheme, counterTheme]) });
    },
    [selKey, chosen, record, first, reasonTheme, counterTheme],
  );
  const moveTheme = useCallback(
    (pos: number, dir: -1 | 1) => {
      const cur = getArg();
      const to = pos + dir;
      if (to < 0 || to >= cur.rank.length) return;
      const rank = [...cur.rank];
      [rank[pos], rank[to]] = [rank[to], rank[pos]];
      const a = { ...cur, rank, ranked: true };
      commitArgue(a);
      recordRank(a);
    },
    [getArg, commitArgue, recordRank],
  );
  const keepOrder = useCallback(() => {
    const a = { ...getArg(), ranked: true };
    commitArgue(a);
    recordRank(a);
  }, [getArg, commitArgue, recordRank]);
  const setParagraphs = useCallback(
    (v: unknown) => {
      if (!isParagraphCount(v)) return;
      const a = { ...getArg(), nparas: v };
      commitArgue(a);
      recordRank(a);
    },
    [getArg, commitArgue, recordRank],
  );
  const recordPosition = useCallback(
    (a: ArgueState) => {
      if (!selKey) return;
      const p = a.pos !== null ? content?.positions[a.pos] : undefined;
      const r = clean(a.ownR) || (p && a.reason !== null ? clean(p.reasons[a.reason]?.text ?? '') : '');
      const c = clean(a.ownC) || (p && a.counter !== null ? clean(p.counters[a.counter]?.text ?? '') : '');
      record(WORKSHOP_KEYS.position(selKey), { step: 'argue', cases: chosen, pos: a.pos, reason: a.reason, counter: a.counter, ownR: a.ownR, ownC: a.ownC, reasonText: r, counterText: c, line: a.line, done: !!a.line.trim() && a.pos !== null && !!c });
    },
    [selKey, chosen, content, record],
  );
  /** Rebuild the thesis from the choices (the learner can still edit it). */
  const rebuilt = useCallback(
    (a: ArgueState): ArgueState => {
      const p = a.pos !== null ? content?.positions[a.pos] : undefined;
      const r = clean(a.ownR) || (p && a.reason !== null ? clean(p.reasons[a.reason]?.text ?? '') : '');
      const c = clean(a.ownC) || (p && a.counter !== null ? clean(p.counters[a.counter]?.text ?? '') : '');
      const line = buildThesis(p, r, c);
      return line ? { ...a, line } : a;
    },
    [content],
  );
  const applyArgue = useCallback(
    (fn: (cur: ArgueState) => ArgueState) => {
      const a = rebuilt(fn(getArg()));
      commitArgue(a);
      recordPosition(a);
    },
    [getArg, rebuilt, commitArgue, recordPosition],
  );
  const choosePosition = useCallback((i: number) => applyArgue((cur) => (cur.pos !== null && cur.pos !== i ? { ...cur, pos: i, reason: null, counter: null, ownR: '', ownC: '', line: '' } : { ...cur, pos: i })), [applyArgue]);
  const chooseReason = useCallback((i: number) => applyArgue((cur) => ({ ...cur, reason: i, ownR: '' })), [applyArgue]);
  const chooseCounter = useCallback((i: number) => applyArgue((cur) => ({ ...cur, counter: i, ownC: '' })), [applyArgue]);
  const setOwnReason = useCallback((text: string) => applyArgue((cur) => ({ ...cur, ownR: text })), [applyArgue]);
  const setOwnCounter = useCallback((text: string) => applyArgue((cur) => ({ ...cur, ownC: text })), [applyArgue]);
  /** The thesis as typed; recorded on `commitThesis` (blur), not every keystroke. */
  const setThesis = useCallback((line: string) => commitArgue({ ...getArg(), line }), [getArg, commitArgue]);
  const commitThesis = useCallback(() => recordPosition(getArg()), [getArg, recordPosition]);

  // --- Step 5: the flashcards -----------------------------------------------
  const getPile = useCallback((): MissedState => {
    const stored = selKey ? latestPile.current[selKey] : undefined;
    return stored && stored.sig === deckSig ? stored : pile;
  }, [selKey, deckSig, pile]);
  const commitPile = useCallback(
    (next: MissedState) => {
      if (!selKey) return;
      latestPile.current = { ...latestPile.current, [selKey]: next };
      setMissedState((s) => ({ ...s, [selKey]: next }));
    },
    [selKey],
  );
  const recordMissed = useCallback(
    (m: MissedState) => {
      if (selKey) record(WORKSHOP_KEYS.missed(selKey), { step: 'missed', cases: chosen, sig: m.sig, deck: deck.length, queue: m.queue, got: m.got, done: m.queue.length === 0 });
    },
    [selKey, chosen, deck.length, record],
  );
  const turnCard = useCallback(() => commitPile({ ...getPile(), flip: true }), [getPile, commitPile]);
  /** Got it: the front card is done. Not yet: it goes to the back of the pile. */
  const answerCard = useCallback(
    (got: boolean) => {
      const cur = getPile();
      if (cur.queue.length === 0) return;
      const [front, ...rest] = cur.queue;
      const next: MissedState = got ? { ...cur, queue: rest, got: [...cur.got, front], flip: false } : { ...cur, queue: [...rest, front], flip: false };
      commitPile(next);
      recordMissed(next);
    },
    [getPile, commitPile, recordMissed],
  );
  // A deck with nothing in it is done the moment the step is reached.
  useEffect(() => {
    if (phase !== 'steps' || step?.id !== 'missed' || !selKey || deck.length > 0) return;
    const stored = missedState[selKey];
    if (stored && stored.sig === deckSig) return;
    const empty: MissedState = { sig: deckSig, queue: [], got: [], flip: false };
    setMissedState((s) => ({ ...s, [selKey]: empty }));
    recordMissed(empty);
  }, [phase, step, selKey, deck.length, deckSig, missedState, recordMissed]);

  // --- Steps 6 to 10: plans and drafts ----------------------------------------
  const latestPlans = useRef<Record<string, Record<number, ParagraphPlan>>>({});
  const latestDrafts = useRef<Record<string, Partial<Record<PartId, DraftState>>>>({});
  useEffect(() => {
    latestPlans.current = plans;
  }, [plans]);
  useEffect(() => {
    latestDrafts.current = drafts;
  }, [drafts]);
  const getPlan = useCallback((theme: number): ParagraphPlan => (selKey && latestPlans.current[selKey]?.[theme]) || emptyPlan(), [selKey]);
  const commitPlan = useCallback(
    (theme: number, next: ParagraphPlan, save = true) => {
      if (!selKey) return;
      latestPlans.current = { ...latestPlans.current, [selKey]: { ...(latestPlans.current[selKey] ?? {}), [theme]: next } };
      setPlans((s) => ({ ...s, [selKey]: { ...(s[selKey] ?? {}), [theme]: next } }));
      if (save) record(WORKSHOP_KEYS.plan(selKey, theme), { step: 'paragraph', cases: chosen, theme, ts: next.ts, own: next.own, sel: next.sel, locked: next.locked, persp: next.persp, noP: next.noP, puse: next.puse, pagree: next.pagree });
    },
    [selKey, chosen, record],
  );
  const chooseTopicSentence = useCallback((theme: number, i: number) => commitPlan(theme, { ...getPlan(theme), ts: i }), [getPlan, commitPlan]);
  /** Type an own topic sentence; it counts once "Use my own sentence" is pressed. */
  const setOwnTopicSentence = useCallback((theme: number, text: string) => commitPlan(theme, { ...getPlan(theme), own: text }, false), [getPlan, commitPlan]);
  /** Record the own sentence as edited (blur), once it is the chosen one; typing never records. */
  const commitOwnTopicSentence = useCallback(
    (theme: number) => {
      const p = getPlan(theme);
      if (p.ts === 'own') commitPlan(theme, p);
    },
    [getPlan, commitPlan],
  );
  const useOwnTopicSentence = useCallback(
    (theme: number) => {
      const p = getPlan(theme);
      if (!clean(p.own)) return;
      commitPlan(theme, { ...p, ts: 'own' });
    },
    [getPlan, commitPlan],
  );
  const toggleFact = useCallback(
    (theme: number, key: string) => {
      const p = getPlan(theme);
      if (p.locked) return;
      const sel = p.sel.includes(key) ? p.sel.filter((k) => k !== key) : [...p.sel, key];
      commitPlan(theme, { ...p, sel, checked: false }, false);
    },
    [getPlan, commitPlan],
  );
  const [selectionNote, setSelectionNote] = useState<string | null>(null);
  const checkFacts = useCallback(
    (theme: number) => {
      const p = getPlan(theme);
      const c = checkSelection(w, chosen, factsOffered(theme), p.sel);
      setSelectionNote(c.message);
      commitPlan(theme, { ...p, checked: true, locked: c.ok });
    },
    [w, chosen, getPlan, factsOffered, commitPlan],
  );
  const changeFacts = useCallback(
    (theme: number) => {
      setSelectionNote(null);
      commitPlan(theme, { ...getPlan(theme), checked: false, locked: false });
    },
    [getPlan, commitPlan],
  );
  const choosePerspective = useCallback((theme: number, i: number) => commitPlan(theme, { ...getPlan(theme), persp: i, noP: false }), [getPlan, commitPlan]);
  const leavePerspective = useCallback((theme: number) => commitPlan(theme, { ...getPlan(theme), persp: null, noP: true, puse: null, pagree: null }), [getPlan, commitPlan]);
  const setPerspectiveUse = useCallback((theme: number, v: number) => commitPlan(theme, { ...getPlan(theme), puse: v }), [getPlan, commitPlan]);
  const setPerspectiveAgree = useCallback((theme: number, v: number) => commitPlan(theme, { ...getPlan(theme), pagree: v }), [getPlan, commitPlan]);
  // The note belongs to one paragraph's facts screen.
  useEffect(() => {
    setSelectionNote(null);
  }, [step, part]);

  const getDraft = useCallback((part: PartId): DraftState => (selKey && latestDrafts.current[selKey]?.[part]) || { text: '', checked: false, hand: false }, [selKey]);
  const draftTimers = useRef<Record<string, ReturnType<typeof setTimeout>>>({});
  /** The save each pending timer would run, so an unmount saves it instead of dropping it. */
  const draftFlush = useRef<Record<string, () => void>>({});
  const saveDraft = useCallback(
    (part: PartId, d: DraftState) => {
      if (!selKey) return;
      const key = `${selKey}:${part}`;
      if (draftTimers.current[key]) {
        clearTimeout(draftTimers.current[key]);
        delete draftTimers.current[key];
        delete draftFlush.current[key];
      }
      onDraft?.(WORKSHOP_KEYS.draft(selKey, part), { step: 'write', cases: chosen, part, text: d.text, checked: d.checked, hand: d.hand, words: wordCount(d.text) });
    },
    [selKey, chosen, onDraft],
  );
  const commitDraft = useCallback(
    (part: PartId, next: DraftState, save: 'now' | 'later' | 'never') => {
      if (!selKey) return;
      latestDrafts.current = { ...latestDrafts.current, [selKey]: { ...(latestDrafts.current[selKey] ?? {}), [part]: next } };
      setDrafts((s) => ({ ...s, [selKey]: { ...(s[selKey] ?? {}), [part]: next } }));
      if (save === 'now') saveDraft(part, next);
      else if (save === 'later') {
        const key = `${selKey}:${part}`;
        draftFlush.current[key] = () => saveDraft(part, getDraft(part));
        if (!draftTimers.current[key]) draftTimers.current[key] = setTimeout(() => draftFlush.current[key]?.(), DRAFT_SAVE_MS);
      }
    },
    [selKey, saveDraft, getDraft],
  );
  /** The text as typed: saved after a pause, or on `flushDraft` (blur, step change). */
  const setDraftText = useCallback((part: PartId, text: string) => commitDraft(part, { ...getDraft(part), text }, 'later'), [getDraft, commitDraft]);
  const flushDraft = useCallback(
    (part: PartId) => {
      if (!selKey) return;
      if (draftTimers.current[`${selKey}:${part}`]) saveDraft(part, getDraft(part));
    },
    [selKey, saveDraft, getDraft],
  );
  const toggleHand = useCallback((part: PartId, hand: boolean) => commitDraft(part, { ...getDraft(part), hand }, 'now'), [getDraft, commitDraft]);
  /** The quick check: rules against the plan, never a mark. */
  const checkPart = useCallback(
    (part: PartId): CheckItem[] => {
      if (!selKey || !content || !first) return [];
      const d = getDraft(part);
      const text = d.text.trim();
      if (!text) return [];
      const ctx = { w, chosen, e: first, checks: content.checks, paragraphs: paragraphs ?? 4 } as const;
      let items: CheckItem[];
      if (part === 'intro') items = checkIntro(text, ctx);
      else if (part === 'conclusion') items = checkConclusion(text, ctx);
      else {
        const theme = Number(part.slice(1));
        const p = getPlan(theme);
        const offered = factsOffered(theme);
        const planned = offered.filter((f) => p.locked && p.sel.includes(f.key) && f.relevant);
        const persp = perspectiveOf(theme);
        items = checkParagraph(text, { ...ctx, theme, topicSentence: topicSentenceOf(theme), planned, perspective: persp ? { name: persp.name, match: persp.match } : null });
      }
      setChecks((s) => ({ ...s, [selKey]: { ...(s[selKey] ?? {}), [part]: items } }));
      commitDraft(part, { ...d, checked: true }, 'now');
      return items;
    },
    [selKey, content, first, w, chosen, paragraphs, getDraft, getPlan, factsOffered, perspectiveOf, topicSentenceOf, commitDraft],
  );
  const checkItemsOf = useCallback((part: PartId): CheckItem[] | null => (selKey && checks[selKey]?.[part]) || null, [checks, selKey]);

  // --- The essay: the parts assembled, and starting again ------------------
  const essayText = useMemo(() => assembleEssay(essayOrder.map((p) => draftOf(p).text)), [essayOrder, draftOf]);
  const essayWritten = useMemo(() => essayOrder.filter((p) => !!draftOf(p).text.trim()).length, [essayOrder, draftOf]);
  /** The parts the learner ticked as written by hand, by title, in essay order. */
  const handwritten = useMemo(() => essayOrder.filter((p) => draftOf(p).hand).map((p) => partTitle(p, paragraphList)), [essayOrder, draftOf, paragraphList]);
  /**
   * Clear everything and start again (the essay screen's last button). Records
   * are append-only, so every key seen (restored or recorded this sitting)
   * gets a `cleared` row that resume skips; a draft is emptied through its
   * upsert. Then the state goes back to a fresh start screen.
   */
  const resetAll = useCallback(() => {
    for (const t of Object.values(draftTimers.current)) clearTimeout(t);
    draftTimers.current = {};
    draftFlush.current = {};
    const keys = new Set<string>([...Object.keys(initialProgress ?? {}), ...seenKeys.current]);
    for (const k of keys) {
      if (k.startsWith('ws:draft:')) onDraft?.(k, { step: 'write', cases: chosen, part: k.split(':').pop(), text: '', checked: false, hand: false, words: 0, cleared: true });
      else record(k, { cleared: true });
    }
    seenKeys.current = new Set();
    latestMatch.current = {};
    latestArgue.current = {};
    latestPile.current = {};
    latestPlans.current = {};
    latestDrafts.current = {};
    lastRecorded.current = fixed ? selectionKey(fixed) : null;
    setTags({});
    setLabelCheck(null);
    setRecall({});
    setRevDone({});
    setRevSkipped({});
    setMatch({});
    setArgue({});
    setMissedState({});
    setPlans({});
    setDrafts({});
    setChecks({});
    setMatchSel(null);
    setMatchNote(null);
    setSelectionNote(null);
    setChosen(fixed ?? []);
    setRefused(null);
    setStepIdx(0);
    setPart(0);
    setPhase('start');
  }, [initialProgress, chosen, fixed, onDraft, record]);
  // Save every pending draft when the player goes away: a focused box gets no
  // blur on unmount (a swipe or key change of slide), so the timer's save runs now.
  useEffect(
    () => () => {
      for (const [key, t] of Object.entries(draftTimers.current)) {
        clearTimeout(t);
        draftFlush.current[key]?.();
      }
      draftTimers.current = {};
      draftFlush.current = {};
    },
    [],
  );

  return {
    steps,
    step,
    stepIdx,
    part,
    partCount,
    phase,
    pick,
    chosen,
    complete,
    selKey,
    refused,
    toggleCase,
    paragraphs,
    setParagraphs,
    isDone,
    isLocked,
    firstOpen,
    held,
    gate,
    begin,
    goTo,
    goToStep,
    next,
    prev,
    toStart,
    // step 1
    tags,
    brush,
    setBrush,
    tagPart,
    labelCheck,
    checkTags,
    // step 2
    parts,
    currentRecall,
    setRecallText,
    checkRecall,
    toggleRecallFact,
    saveRecall,
    changeRecall,
    revDone: !!selKey && !!revDone[selKey],
    revSkipped: !!selKey && !!revSkipped[selKey],
    skipRevision,
    missedByScreen,
    // step 3
    matchCase,
    matchState,
    matchDeal,
    matchSel,
    matchNote,
    matchSolved,
    tapTheme,
    tapEvidence,
    checkMatch,
    showMatches,
    // step 4
    content,
    arg,
    arguePart,
    twoCase,
    cmpDone,
    argDone,
    suggestion,
    reasonText,
    counterText,
    reasonTheme,
    counterTheme,
    paragraphList,
    judge,
    moveTheme,
    keepOrder,
    choosePosition,
    chooseReason,
    chooseCounter,
    setOwnReason,
    setOwnCounter,
    setThesis,
    commitThesis,
    // step 5
    deck,
    pile,
    missedDone,
    turnCard,
    answerCard,
    // steps 6 to 10
    themeOfParagraph,
    planOf,
    topicSentenceOf,
    perspectivesFor,
    perspectiveOf,
    perspDone,
    planDone,
    factsOffered,
    selectionNote,
    chooseTopicSentence,
    setOwnTopicSentence,
    commitOwnTopicSentence,
    useOwnTopicSentence,
    toggleFact,
    checkFacts,
    changeFacts,
    choosePerspective,
    leavePerspective,
    setPerspectiveUse,
    setPerspectiveAgree,
    draftOf,
    partDone,
    setDraftText,
    flushDraft,
    toggleHand,
    checkPart,
    checkItemsOf,
    // the essay
    essayOrder,
    essayText,
    essayWritten,
    handwritten,
    resetAll,
  };
}

export type WorkshopApi = ReturnType<typeof useWorkshop>;
